import { DateTime } from 'luxon';
import moment from 'moment';

export type LocalTimeLike =
  | { hour: number; minute: number; second?: number }
  | null
  | undefined;

// Combine a local date string (YYYY-MM-DD) with a local time (hour/minute) and return UTC ISO 8601 string
export function toUtcIsoFromLocalParts(
  dateISO: string | undefined,
  time: LocalTimeLike,
  options?: { zone?: string }
): string | undefined {
  if (!dateISO || !time) return undefined;
  const { hour, minute, second } = time;
  const zone = options?.zone || undefined; // undefined => system zone
  const dt = DateTime.fromISO(dateISO, { zone })
    .set({
      hour: hour ?? 0,
      minute: minute ?? 0,
      second: second ?? 0,
      millisecond: 0
    })
    .toUTC();
  return dt.toISO({ suppressMilliseconds: true }) || undefined;
}

// Format a UTC ISO timestamp into a local time string like "10:00 AM PST"
export function formatLocalTimeFromUtc(
  utcISO: string | undefined,
  options?: { locale?: string; timeZone?: string }
): string {
  if (!utcISO) return '';
  const d = new Date(utcISO);
  const locale = options?.locale || 'en-US';
  const fmt = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: options?.timeZone
  });
  return fmt.format(d);
}

/**
 * Calendar date as YYYY-MM-DD for an instant in a given IANA time zone (not the UTC calendar date).
 * When `timeZone` is omitted, uses the viewer's browser timezone (`getUserTimezone()`).
 */
export function getYyyyMmDdInTimeZone(
  timeZone: string = getUserTimezone(),
  instant: Date = new Date()
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(instant);
}

/**
 * Adds N business days (skipping Sat/Sun) to a YYYY-MM-DD date, returning YYYY-MM-DD.
 * e.g. Fri 2026-07-17 + 2 business days → Tue 2026-07-21 (Sat/Sun skipped).
 */
export function addBusinessDaysIso(
  isoDate: string,
  businessDays: number
): string {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate;
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(y, m - 1, d); // local date-only math, no TZ shift
  let added = 0;
  while (added < businessDays) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) {
      added++;
    }
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Convert a UTC ISO timestamp to HH:mm string in a target timezone
export function utcIsoToLocal24(
  utcISO: string | undefined,
  timezone?: string
): string {
  if (!utcISO) return '';
  try {
    const dt = DateTime.fromISO(utcISO, { zone: 'utc' }).setZone(
      timezone || 'system'
    );
    return `${String(dt.hour).padStart(2, '0')}:${String(dt.minute).padStart(
      2,
      '0'
    )}`;
  } catch {
    return '';
  }
}

// Convert a UTC label time like "8:30 AM" to HH:mm in a target timezone, using a base date
export function utcLabelTimeToLocal24(
  baseDateISOOrUtcISO: string | undefined,
  utcLabelTime: string | undefined,
  timezone?: string
): string {
  if (!baseDateISOOrUtcISO || !utcLabelTime) return '';
  const m = utcLabelTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return '';
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const ampm = (m[3] || '').toUpperCase();
  if (ampm === 'PM' && hour !== 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;

  try {
    // Determine base UTC date in UTC zone
    const base = DateTime.fromISO(baseDateISOOrUtcISO, { zone: 'utc' });
    const baseUtc = base.isValid
      ? base
      : DateTime.fromISO(baseDateISOOrUtcISO.split('T')[0], { zone: 'utc' });
    const utcDateTime = baseUtc.set({
      hour,
      minute,
      second: 0,
      millisecond: 0
    });
    const local = utcDateTime.setZone(timezone || 'system');
    return `${String(local.hour).padStart(2, '0')}:${String(
      local.minute
    ).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

// Format time range in UTC format like "11:11 AM - 12:12 PM UTC"
// Converts local time inputs to UTC and formats them
// NOTE: This function is kept for backward compatibility with existing data
// New code should use formatLocalTimeRange instead
export function formatUtcTimeRange(
  startTime24: string,
  endTime24: string = '',
  dateISO?: string,
  timezone?: string
): string {
  if (!startTime24) return '';

  // If we have a date, convert local times to UTC
  if (dateISO) {
    try {
      const [startHour, startMinute] = startTime24.split(':').map(Number);
      const startUtcIso = toUtcIsoFromLocalParts(
        dateISO,
        {
          hour: startHour,
          minute: startMinute
        },
        timezone ? { zone: timezone } : undefined
      );

      if (startUtcIso) {
        const startUtcDate = new Date(startUtcIso);
        const startHour12 =
          startUtcDate.getUTCHours() === 0
            ? 12
            : startUtcDate.getUTCHours() > 12
            ? startUtcDate.getUTCHours() - 12
            : startUtcDate.getUTCHours();
        const startAmPm = startUtcDate.getUTCHours() >= 12 ? 'PM' : 'AM';
        // Format without space after time: "11:11AM" not "11:11 AM"
        const startFormatted = `${startHour12}:${String(
          startUtcDate.getUTCMinutes()
        ).padStart(2, '0')}${startAmPm}`;

        // If no end time, return just start time with UTC
        if (!endTime24) {
          return `${startFormatted} UTC`;
        }

        // Convert end time to UTC
        const [endHour, endMinute] = endTime24.split(':').map(Number);
        const endUtcIso = toUtcIsoFromLocalParts(
          dateISO,
          {
            hour: endHour,
            minute: endMinute
          },
          timezone ? { zone: timezone } : undefined
        );

        if (endUtcIso) {
          const endUtcDate = new Date(endUtcIso);
          const endHour12 =
            endUtcDate.getUTCHours() === 0
              ? 12
              : endUtcDate.getUTCHours() > 12
              ? endUtcDate.getUTCHours() - 12
              : endUtcDate.getUTCHours();
          const endAmPm = endUtcDate.getUTCHours() >= 12 ? 'PM' : 'AM';
          // Format without space after time: "12:12PM" not "12:12 PM"
          const endFormatted = `${endHour12}:${String(
            endUtcDate.getUTCMinutes()
          ).padStart(2, '0')}${endAmPm}`;

          return `${startFormatted} - ${endFormatted} UTC`;
        }
      }
    } catch (e) {
      // Fall back to simple formatting if conversion fails
    }
  }

  // Fallback: format as local time with UTC label (for backward compatibility)
  // Backend expects format: "11:11AM - 12:12PM UTC" (no spaces after AM/PM)
  const [startHour, startMinute] = startTime24.split(':').map(Number);
  const startHour12 =
    startHour === 0 ? 12 : startHour > 12 ? startHour - 12 : startHour;
  const startAmPm = startHour >= 12 ? 'PM' : 'AM';
  // Format without space after time: "11:11AM" not "11:11 AM"
  const startFormatted = `${startHour12}:${String(startMinute).padStart(
    2,
    '0'
  )}${startAmPm}`;

  // If no end time, return just start time with UTC
  if (!endTime24) {
    return `${startFormatted} UTC`;
  }

  // Parse end time
  const [endHour, endMinute] = endTime24.split(':').map(Number);
  const endHour12 = endHour === 0 ? 12 : endHour > 12 ? endHour - 12 : endHour;
  const endAmPm = endHour >= 12 ? 'PM' : 'AM';
  // Format without space after time: "12:12PM" not "12:12 PM"
  const endFormatted = `${endHour12}:${String(endMinute).padStart(
    2,
    '0'
  )}${endAmPm}`;

  return `${startFormatted} - ${endFormatted} UTC`;
}

// Get user's browser timezone
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/Los_Angeles'; // Fallback to PST
  }
}

// Get short timezone abbreviation (PST, IST, EST, etc.)
export function getTimezoneAbbreviation(timezone?: string): string {
  try {
    const tz = timezone || getUserTimezone();

    // Enhanced timezone mapping for consistent cross-browser behavior
    // Chrome often returns "GMT+X:XX" while Safari returns friendly names
    const timezoneMap: Record<string, string> = {
      // Asia
      'Asia/Kolkata': 'IST',
      'Asia/Calcutta': 'IST',
      'Asia/Tokyo': 'JST',
      'Asia/Shanghai': 'CST',
      'Asia/Hong_Kong': 'HKT',
      'Asia/Singapore': 'SGT',
      'Asia/Seoul': 'KST',
      'Asia/Dubai': 'GST',
      'Asia/Karachi': 'PKT',
      // Europe
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Europe/Berlin': 'CET',
      'Europe/Moscow': 'MSK',
      // Americas
      'America/New_York': 'EST',
      'America/Chicago': 'CST',
      'America/Denver': 'MST',
      'America/Los_Angeles': 'PST',
      'America/Toronto': 'EST',
      'America/Vancouver': 'PST',
      // Pacific
      'Australia/Sydney': 'AEST',
      'Pacific/Auckland': 'NZST',
      // UTC
      UTC: 'UTC',
      GMT: 'GMT'
    };

    // Try direct timezone name mapping first (most reliable)
    if (timezoneMap[tz]) {
      return timezoneMap[tz];
    }

    // Fallback: Try to get from browser (may differ between browsers)
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: tz
    }).formatToParts(new Date());
    const tzName = parts.find(p => p.type === 'timeZoneName')?.value || 'Local';

    // If browser returns GMT format, map it to friendly abbreviation
    if (tzName.includes('GMT')) {
      // Map common GMT offsets to timezone abbreviations
      const gmtOffsetMap: Record<string, string> = {
        'GMT+5:30': 'IST',
        'GMT+05:30': 'IST',
        'GMT+5': 'PKT',
        'GMT+8': 'SGT',
        'GMT+9': 'JST',
        'GMT+10': 'AEST',
        'GMT-8': 'PST',
        'GMT-7': 'MST',
        'GMT-6': 'CST',
        'GMT-5': 'EST',
        'GMT+0': 'GMT',
        'GMT+1': 'CET',
        GMT: 'GMT'
      };

      // Try to match the GMT offset string
      for (const [offset, abbr] of Object.entries(gmtOffsetMap)) {
        if (tzName.includes(offset)) {
          return abbr;
        }
      }

      // If no mapping found, return the GMT string as-is
      return tzName;
    }

    return tzName;
  } catch {
    return 'Local';
  }
}

// Convert GMT offset notation to user-friendly timezone abbreviation
export function normalizeTimezoneInString(timeString: string): string {
  if (!timeString) return timeString;

  // Map GMT offsets to user-friendly abbreviations
  const gmtReplacements: Record<string, string> = {
    'GMT+5:30': 'IST',
    'GMT+05:30': 'IST',
    'GMT+5': 'PKT',
    'GMT+8': 'SGT',
    'GMT+9': 'JST',
    'GMT+10': 'AEST',
    'GMT-8': 'PST',
    'GMT-7': 'MST',
    'GMT-6': 'CST',
    'GMT-5': 'EST',
    'GMT+0': 'GMT',
    'GMT+1': 'CET',
    GMT: 'GMT'
  };

  let result = timeString;
  for (const [gmtOffset, abbreviation] of Object.entries(gmtReplacements)) {
    result = result.replace(new RegExp(gmtOffset, 'g'), abbreviation);
  }

  return result;
}

// Format time range in user's local timezone like "11:11 AM - 12:12 PM PST"
// Stores times in UTC internally but displays in user's timezone
export function formatLocalTimeRange(
  startTime24: string,
  endTime24: string = '',
  dateISO?: string,
  timezone?: string
): string {
  if (!startTime24) return '';

  const tz = timezone || getUserTimezone();
  const tzAbbr = getTimezoneAbbreviation(tz);

  // If we don't have a date, just format the times as-is with timezone label
  // Backend expects format: "11:11AM - 12:12PM PST" (no spaces after AM/PM)
  if (!dateISO) {
    const [startHour, startMinute] = startTime24.split(':').map(Number);
    const startHour12 =
      startHour === 0 ? 12 : startHour > 12 ? startHour - 12 : startHour;
    const startAmPm = startHour >= 12 ? 'PM' : 'AM';
    // Format without space after time: "11:11AM" not "11:11 AM"
    const startFormatted = `${startHour12}:${String(startMinute).padStart(
      2,
      '0'
    )}${startAmPm}`;

    if (!endTime24) {
      return `${startFormatted} ${tzAbbr}`;
    }

    const [endHour, endMinute] = endTime24.split(':').map(Number);
    const endHour12 =
      endHour === 0 ? 12 : endHour > 12 ? endHour - 12 : endHour;
    const endAmPm = endHour >= 12 ? 'PM' : 'AM';
    // Format without space after time: "12:12PM" not "12:12 PM"
    const endFormatted = `${endHour12}:${String(endMinute).padStart(
      2,
      '0'
    )}${endAmPm}`;

    return `${startFormatted} - ${endFormatted} ${tzAbbr}`;
  }

  // Format times in local timezone (already in local timezone, just need to format)
  // Backend expects format: "11:11AM - 12:12PM PST" (no spaces after AM/PM)
  try {
    const [startHour, startMinute] = startTime24.split(':').map(Number);
    const startHour12 =
      startHour === 0 ? 12 : startHour > 12 ? startHour - 12 : startHour;
    const startAmPm = startHour >= 12 ? 'PM' : 'AM';
    // Format without space after time: "11:11AM" not "11:11 AM"
    const startFormatted = `${startHour12}:${String(startMinute).padStart(
      2,
      '0'
    )}${startAmPm}`;

    if (!endTime24) {
      return `${startFormatted} ${tzAbbr}`;
    }

    const [endHour, endMinute] = endTime24.split(':').map(Number);
    const endHour12 =
      endHour === 0 ? 12 : endHour > 12 ? endHour - 12 : endHour;
    const endAmPm = endHour >= 12 ? 'PM' : 'AM';
    // Format without space after time: "12:12PM" not "12:12 PM"
    const endFormatted = `${endHour12}:${String(endMinute).padStart(
      2,
      '0'
    )}${endAmPm}`;

    return `${startFormatted} - ${endFormatted} ${tzAbbr}`;
  } catch (e) {
    // Fallback to simple formatting
    return `${startTime24} ${tzAbbr}`;
  }
}

/**
 * Format a date string to "Month Day, Year" format (e.g., "January 22, 2025")
 * @param date - Date string in any format that moment can parse (ISO, YYYY-MM-DD, etc.)
 * @returns Formatted date string or empty string if invalid
 *
 * @example
 * formatToMonthDayYear('2025-01-22') // Returns "January 22, 2025"
 * formatToMonthDayYear('2025-01-22T10:30:00Z') // Returns "January 22, 2025"
 */
export function formatToMonthDayYear(date: string | undefined | null): string {
  if (!date) return '';

  const momentDate = moment(date);

  if (!momentDate.isValid()) return '';

  return momentDate.format('MMMM D, YYYY');
}

export type FiscalQuarterInfo = {
  quarter: 1 | 2 | 3 | 4;
  labelYear: number;
  startYear: number;
  startMonthIndex: number; // 0-based month index
  endYear: number;
  endMonthIndex: number; // 0-based month index
};

/**
 * Parse quarter label using fiscal-quarter mapping:
 * - Q1 YYYY => Dec(YYYY-1), Jan(YYYY), Feb(YYYY)
 * - Q2 YYYY => Mar-May YYYY
 * - Q3 YYYY => Jun-Aug YYYY
 * - Q4 YYYY => Sep-Nov YYYY
 */
export function parseFiscalQuarterLabel(
  quarterLabel: string
): FiscalQuarterInfo | null {
  const match = quarterLabel.match(/^Q([1-4])\s+(\d{4})$/);
  if (!match) return null;

  const quarter = parseInt(match[1], 10) as 1 | 2 | 3 | 4;
  const labelYear = parseInt(match[2], 10);

  switch (quarter) {
    case 1:
      return {
        quarter,
        labelYear,
        startYear: labelYear - 1,
        startMonthIndex: 11, // Dec
        endYear: labelYear,
        endMonthIndex: 1 // Feb
      };
    case 2:
      return {
        quarter,
        labelYear,
        startYear: labelYear,
        startMonthIndex: 2, // Mar
        endYear: labelYear,
        endMonthIndex: 4 // May
      };
    case 3:
      return {
        quarter,
        labelYear,
        startYear: labelYear,
        startMonthIndex: 5, // Jun
        endYear: labelYear,
        endMonthIndex: 7 // Aug
      };
    case 4:
      return {
        quarter,
        labelYear,
        startYear: labelYear,
        startMonthIndex: 8, // Sep
        endYear: labelYear,
        endMonthIndex: 10 // Nov
      };
    default:
      return null;
  }
}

export function getFiscalQuarterStartDate(quarterLabel: string): Date | null {
  const quarterInfo = parseFiscalQuarterLabel(quarterLabel);
  if (!quarterInfo) return null;
  return new Date(quarterInfo.startYear, quarterInfo.startMonthIndex, 1);
}

export function getFiscalQuarterEndDate(quarterLabel: string): Date | null {
  const quarterInfo = parseFiscalQuarterLabel(quarterLabel);
  if (!quarterInfo) return null;
  return new Date(quarterInfo.endYear, quarterInfo.endMonthIndex + 1, 0);
}

export function isFiscalQuarterInPast(
  quarterLabel: string,
  now: Date = new Date()
): boolean {
  const quarterEnd = getFiscalQuarterEndDate(quarterLabel);
  if (!quarterEnd) return false;

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  quarterEnd.setHours(0, 0, 0, 0);
  return quarterEnd < today;
}

/**
 * Format a quarter string to add comma between quarter and year
 * @param quarter - Quarter string in format "Q1 2025" or similar
 * @returns Formatted quarter string with comma (e.g., "Q1, 2025")
 *
 * @example
 * formatQuarter('Q4 2025') // Returns "Q4, 2025"
 * formatQuarter('Q1 2024') // Returns "Q1, 2024"
 */
export function formatQuarter(quarter: string | undefined | null): string {
  if (!quarter) return '';

  // Match pattern like "Q1 2025" or "Q1-2025" or "Q12025"
  const match = quarter.match(/^(Q[1-4])\s*[-\s]?\s*(\d{4})$/i);

  if (match) {
    return `${match[1]}, ${match[2]}`;
  }

  // Return as-is if pattern doesn't match
  return quarter;
}

/**
 * Format a month string to full month name with comma
 * @param monthStr - Month string in format "Oct 2025", "October 2025", "2025-10", etc.
 * @returns Formatted month string (e.g., "October, 2025")
 *
 * @example
 * formatMonth('Oct 2025') // Returns "October, 2025"
 * formatMonth('October 2025') // Returns "October, 2025"
 * formatMonth('2025-10') // Returns "October, 2025"
 */
export function formatMonth(monthStr: string | undefined | null): string {
  if (!monthStr) return '';

  // Try to parse with moment
  const parsed = moment(
    monthStr,
    [
      'MMM YYYY', // Oct 2025
      'MMMM YYYY', // October 2025
      'YYYY-MM', // 2025-10
      'MM-YYYY', // 10-2025
      'MM/YYYY' // 10/2025
    ],
    true
  );

  if (parsed.isValid()) {
    return parsed.format('MMMM, YYYY');
  }

  // Return as-is if parsing fails
  return monthStr;
}

/**
 * Store a date/time with timezone information
 * Converts local date/time to a format that preserves timezone for backend storage
 *
 * @param dateISO - Date string in YYYY-MM-DD format
 * @param time24 - Time string in HH:mm format (24-hour)
 * @param timezone - IANA timezone (e.g., "America/Los_Angeles", "Asia/Kolkata")
 * @returns Object with ISO timestamp and timezone metadata
 *
 * @example
 * storeWithTimezone('2025-10-17', '14:30', 'America/Los_Angeles')
 * // Returns: {
 * //   isoTimestamp: "2025-10-17T14:30:00-07:00",
 * //   timezone: "America/Los_Angeles",
 * //   timezoneAbbr: "PDT",
 * //   utcTimestamp: "2025-10-17T21:30:00Z"
 * // }
 */
export function storeWithTimezone(
  dateISO: string,
  time24: string,
  timezone?: string
): {
  isoTimestamp: string;
  timezone: string;
  timezoneAbbr: string;
  utcTimestamp: string;
} | null {
  if (!dateISO || !time24) return null;

  try {
    const tz = timezone || getUserTimezone();
    const [hour, minute] = time24.split(':').map(Number);

    // Create DateTime in user's local timezone
    const dt = DateTime.fromISO(dateISO, { zone: tz }).set({
      hour: hour ?? 0,
      minute: minute ?? 0,
      second: 0,
      millisecond: 0
    });

    return {
      isoTimestamp: dt.toISO({ includeOffset: true }) || '', // e.g., "2025-10-17T14:30:00-07:00"
      timezone: tz, // e.g., "America/Los_Angeles"
      timezoneAbbr: getTimezoneAbbreviation(tz), // e.g., "PDT"
      utcTimestamp: dt.toUTC().toISO({ suppressMilliseconds: true }) || '' // e.g., "2025-10-17T21:30:00Z"
    };
  } catch (e) {
    console.error('Error storing with timezone:', e);
    return null;
  }
}

/**
 * Convert stored timezone-aware timestamp to viewer's local timezone
 *
 * @param storedData - Object with isoTimestamp (or utcTimestamp) and timezone
 * @param viewerTimezone - Optional viewer's timezone (defaults to system timezone)
 * @returns Formatted time string in viewer's timezone
 *
 * @example
 * // Data stored as PST: { utcTimestamp: "2025-10-17T21:30:00Z", timezone: "America/Los_Angeles" }
 * // Viewer in IST sees:
 * convertToViewerTimezone(storedData, 'Asia/Kolkata')
 * // Returns: "3:00 AM IST" (next day)
 */
export function convertToViewerTimezone(
  storedData: {
    utcTimestamp?: string;
    isoTimestamp?: string;
    timezone?: string;
  },
  viewerTimezone?: string
): {
  time24: string;
  time12: string;
  date: string;
  timezone: string;
  timezoneAbbr: string;
} | null {
  if (!storedData || (!storedData.utcTimestamp && !storedData.isoTimestamp)) {
    return null;
  }

  try {
    const viewerTz = viewerTimezone || getUserTimezone();
    const viewerTzAbbr = getTimezoneAbbreviation(viewerTz);

    // Parse the stored timestamp (prefer UTC, fallback to ISO with offset)
    const utcDt = storedData.utcTimestamp
      ? DateTime.fromISO(storedData.utcTimestamp, { zone: 'utc' })
      : DateTime.fromISO(storedData.isoTimestamp!);

    // Convert to viewer's timezone
    const viewerDt = utcDt.setZone(viewerTz);

    return {
      time24: viewerDt.toFormat('HH:mm'), // "14:30"
      time12: viewerDt.toFormat('h:mm a'), // "2:30 PM"
      date: viewerDt.toFormat('yyyy-MM-dd'), // "2025-10-17"
      timezone: viewerTz, // "Asia/Kolkata"
      timezoneAbbr: viewerTzAbbr // "IST"
    };
  } catch (e) {
    console.error('Error converting to viewer timezone:', e);
    return null;
  }
}

/**
 * Parse and normalize stored milestone time data for display
 * Handles various stored formats (UTC string, timezone object, legacy formats)
 *
 * @param storedTime - Stored time data from backend
 * @param storedDate - Stored date data from backend
 * @returns Normalized time data for display in user's timezone
 */
export function parseStoredMilestoneTime(
  storedTime: any,
  storedDate?: string
): { startTime: string; endTime: string; timezone: string } | null {
  if (!storedTime) return null;

  try {
    // Case 1: New format with timezone object
    if (typeof storedTime === 'object' && storedTime.utcTimestamp) {
      const converted = convertToViewerTimezone(storedTime);
      if (converted) {
        return {
          startTime: converted.time24,
          endTime: '', // Single time, no range
          timezone: converted.timezoneAbbr
        };
      }
    }

    // Case 2: Time range string like "11:00AM - 12:00PM PST" or "11:00 AM - 12:00 PM PST"
    if (typeof storedTime === 'string' && storedTime.includes('-')) {
      // Extract times and try to parse (supports both "11:00AM" and "11:00 AM" formats)
      const match = storedTime.match(
        /(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*(\w+)?/i
      );
      if (match) {
        const [
          _,
          startHour,
          startMin,
          startAMPM,
          endHour,
          endMin,
          endAMPM,
          tz
        ] = match;

        // Convert to 24-hour format
        let start24 = parseInt(startHour);
        if (startAMPM.toUpperCase() === 'PM' && start24 !== 12) start24 += 12;
        if (startAMPM.toUpperCase() === 'AM' && start24 === 12) start24 = 0;

        let end24 = parseInt(endHour);
        if (endAMPM.toUpperCase() === 'PM' && end24 !== 12) end24 += 12;
        if (endAMPM.toUpperCase() === 'AM' && end24 === 12) end24 = 0;

        return {
          startTime: `${String(start24).padStart(2, '0')}:${startMin}`,
          endTime: `${String(end24).padStart(2, '0')}:${endMin}`,
          timezone: tz || getTimezoneAbbreviation()
        };
      }
    }

    // Case 3: Legacy UTC format - convert to user's local timezone
    if (typeof storedTime === 'string') {
      // If it's a UTC timestamp, convert it
      if (storedTime.includes('UTC') || storedTime.includes('GMT')) {
        // This is a legacy format, try to extract and convert
        return null; // Let the component handle it
      }
    }

    return null;
  } catch (e) {
    console.error('Error parsing stored milestone time:', e);
    return null;
  }
}

/**
 * Convert UTC timestamp to local timezone with meeting duration
 * Extracts duration from time range string and converts UTC timestamp to user's local timezone
 *
 * This is a shared utility for milestone time conversion to eliminate duplicate logic
 * Used by both hover popup and cell display formatting
 *
 * @param timestampToUse - UTC ISO timestamp (e.g., "2025-10-17T21:30:00Z")
 * @param meetingScheduledAt - Time range string (e.g., "2:30PM - 4:00PM PST")
 * @param includeDate - Whether to include converted date in the result
 * @param options - Additional options for formatting
 * @returns Converted time data in user's local timezone, or null if conversion fails
 *
 * @example
 * const result = convertMilestoneTimestampToLocal(
 *   "2025-10-17T21:30:00Z",
 *   "2:30PM - 4:00PM PST",
 *   true
 * );
 * // Returns: {
 * //   timeString: "3:00 AM - 4:30 AM IST",
 * //   convertedDate: "2025-10-18",
 * //   durationMinutes: 90
 * // }
 */
export function convertMilestoneTimestampToLocal(
  timestampToUse: string | undefined,
  meetingScheduledAt: string | undefined,
  includeDate: boolean = false,
  options: {
    defaultDurationMinutes?: number;
    formatWithSpaces?: boolean; // If true: "2:30 PM", if false: "2:30PM"
    use24HourFormat?: boolean; // If true: "14:30", if false: "2:30 PM"
    includeTimezone?: boolean; // If true: append timezone, if false: omit timezone
  } = {}
): {
  timeString: string;
  convertedDate?: string;
  durationMinutes: number;
} | null {
  // Validate inputs
  if (!timestampToUse || !/^\d{4}-\d{2}-\d{2}T/.test(timestampToUse)) {
    return null;
  }

  const defaultDuration = options.defaultDurationMinutes || 60; // Default 1 hour
  const formatWithSpaces = options.formatWithSpaces !== false; // Default to spaces
  const use24HourFormat = options.use24HourFormat || false; // Default to 12-hour format
  const includeTimezone = options.includeTimezone !== false; // Default to include timezone

  try {
    // Step 1: Extract duration from meetingScheduledAt time range
    let durationMinutes = defaultDuration;

    if (
      meetingScheduledAt &&
      meetingScheduledAt.trim() !== '' &&
      !meetingScheduledAt.includes('NaN')
    ) {
      const timeRangeMatch = meetingScheduledAt.match(
        /(\d{1,2}):(\d{2})\s*(AM|PM)?\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)?/i
      );

      if (timeRangeMatch) {
        const [
          ,
          startHourStr,
          startMin,
          startAMPM,
          endHourStr,
          endMin,
          endAMPM
        ] = timeRangeMatch;

        // Convert start time to 24-hour format
        let startHour24 = parseInt(startHourStr);
        if (startAMPM) {
          if (startAMPM.toUpperCase() === 'PM' && startHour24 !== 12)
            startHour24 += 12;
          if (startAMPM.toUpperCase() === 'AM' && startHour24 === 12)
            startHour24 = 0;
        }

        // Convert end time to 24-hour format
        let endHour24 = parseInt(endHourStr);
        if (endAMPM) {
          if (endAMPM.toUpperCase() === 'PM' && endHour24 !== 12)
            endHour24 += 12;
          if (endAMPM.toUpperCase() === 'AM' && endHour24 === 12) endHour24 = 0;
        }

        // Calculate duration in minutes
        const startMinutes = startHour24 * 60 + parseInt(startMin);
        const endMinutes = endHour24 * 60 + parseInt(endMin);
        durationMinutes = endMinutes - startMinutes;
        if (durationMinutes < 0) durationMinutes += 24 * 60; // Handle overnight meetings
      }
    }

    // Step 2: Convert UTC timestamp to user's local timezone
    const startDate = new Date(timestampToUse);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

    // Validate dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return null;
    }

    // Step 3: Format time string based on options
    let timeString: string;

    if (use24HourFormat) {
      // 24-hour format: "14:30 - 15:30"
      const startHour24 = startDate.getHours();
      const startMinute = startDate.getMinutes();
      const endHour24 = endDate.getHours();
      const endMinute = endDate.getMinutes();

      timeString = `${String(startHour24).padStart(2, '0')}:${String(
        startMinute
      ).padStart(2, '0')} - ${String(endHour24).padStart(2, '0')}:${String(
        endMinute
      ).padStart(2, '0')}`;
    } else {
      // 12-hour format: "2:30 PM - 3:30 PM"
      const startHour24 = startDate.getHours();
      const startMinute = startDate.getMinutes();
      const startHour12 =
        startHour24 === 0
          ? 12
          : startHour24 > 12
          ? startHour24 - 12
          : startHour24;
      const startAMPM = startHour24 >= 12 ? 'PM' : 'AM';

      const endHour24 = endDate.getHours();
      const endMinute = endDate.getMinutes();
      const endHour12 =
        endHour24 === 0 ? 12 : endHour24 > 12 ? endHour24 - 12 : endHour24;
      const endAMPM = endHour24 >= 12 ? 'PM' : 'AM';

      const space = formatWithSpaces ? ' ' : '';
      timeString = `${startHour12}:${String(startMinute).padStart(
        2,
        '0'
      )}${space}${startAMPM} - ${endHour12}:${String(endMinute).padStart(
        2,
        '0'
      )}${space}${endAMPM}`;
    }

    // Step 4: Optionally append timezone
    if (includeTimezone) {
      const userTzAbbr = getTimezoneAbbreviation();
      timeString += ` ${userTzAbbr}`;
    }

    // Step 5: Optionally include converted date
    const result: {
      timeString: string;
      convertedDate?: string;
      durationMinutes: number;
    } = {
      timeString,
      durationMinutes
    };

    if (includeDate) {
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const day = String(startDate.getDate()).padStart(2, '0');
      result.convertedDate = `${year}-${month}-${day}`;
    }

    return result;
  } catch (error) {
    console.error('Milestone timestamp conversion error:', error);
    return null;
  }
}
